import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ReactNode } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import SliderOver from '../SliderOver';
import { TshirtTeam } from '../TshirtTeam';

import {
  MasPinesIcon,
  MedalIcon,
  PencilIcon,
  PremiumCrown,
  PremiumIcon,
} from './Icons';
import MenuLogo from './MenuLogo';

import IconAlert from '@/assets/ic_alert_navbar.svg';
import useAuth from '@/hooks/useAuth';
import useCountMedals from '@/hooks/useCountMedals';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import { cn, nameAvatar } from '@/utils';

type ItemsChild = {
  name: string;
  href: string;
  icon?: ReactNode;
  disabled?: boolean;
  highlight?: boolean;
  children?: ItemsChild[];
};

type Props = {
  isOpen: boolean;
  handleOpen: (val: boolean) => void;
  isActiveLink: boolean;
  navigation: {
    name: string;
    href: string;
    icon?: ReactNode;
    highlight?: boolean;
    children?: ItemsChild[];
  }[];
  bottomNavigation: {
    name: string;
    href: string;
    icon?: ReactNode;
    onClick?: () => void;
    isContrast?: boolean;
  }[];
};

const Mobile = ({
  isOpen,
  handleOpen,
  navigation,
  bottomNavigation,
  isActiveLink,
}: Props) => {
  const { userInitialData: userData } = useAuth();
  const { pinsData } = usePinsAccount();
  const { medalsData } = useCountMedals();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { data: dataTshirtShield } = useTshirtAndShield();

  return (
    <SliderOver
      closeIconClassName="text-white"
      handleClose={() => handleOpen(false)}
      open={isOpen}
    >
      <div className="flex flex-col">
        <header
          className={cn('py-8 pl-8 bg-gdt-blue-header space-y-6', {
            'bg-gdt-gray premium': pinsData.isPremium,
          })}
        >
          <div className="flex items-start">
            <div className="relative">
              <figure
                className={cn(
                  ' rounded-full overflow-hidden relative w-[57px] h-[57px] bg-gdt-blue-xl2 text-gdt-blue-header text-[27px] font-bold flex items-center justify-center border-white border-2',
                  {
                    'border-gdt-status-yellow text-gdt-status-yellow bg-gdt-status-yellow-light':
                      pinsData.isPremium,
                  }
                )}
                onClick={() =>
                  location.pathname !== ROUTES.editAccount.home
                    ? navigate(ROUTES.editAccount.myData)
                    : handleOpen(false)
                }
              >
                {userData?.pathImagenPerfil && (
                  <img
                    className="absolute min-w-[57px] min-h-[57px]"
                    src={userData?.pathImagenPerfil}
                  />
                )}
                <figcaption>
                  {userData && nameAvatar(userData.nombre_dt)}
                </figcaption>
              </figure>
              <span
                className={cn(
                  'absolute z-10 -bottom-[2px] -right-[2px] h-5 w-5 bg-gdt-blue rounded-full flex justify-center items-center border-[1px] border-white',
                  { 'bg-gdt-status-yellow': pinsData.isPremium }
                )}
              >
                <PencilIcon className="max-w-[13px] relative top-[-1px]" />
              </span>
            </div>
            <div className="space-y-1 pl-3">
              <h3 className="text-base font-semibold text-white">
                {userData?.nombre_dt}
              </h3>
              <p className="text-xs text-white font-normal pb-1">
                {userData && userData.nombre_equipo}
              </p>
              {pinsData.isPremium && (
                <div className="flex items-center space-x-1 border-gdt-status-yellow rounded border-2 bg-gdt-status-yellow-light justify-center h-[22px] max-w-[6.25rem]">
                  <PremiumCrown />
                  <p className="text-gdt-status-yellow font-bold text-3xs ">
                    DT PREMIUM
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-start">
            <div className="pr-5">
              <MenuLogo className="max-w-[32px] w-full" />
            </div>
            <div
              className={cn('border-l border-[#3366FF] space-y-1 pl-4', {
                'border-gdt-gray-3': pinsData.isPremium,
              })}
            >
              <p className="text-sm text-white font-semibold">Medallas</p>
              <div className="flex items-center gap-1">
                <div>
                  <MedalIcon
                    variant={pinsData.isPremium ? 'premium' : 'quaternary'}
                  />
                </div>
                <p className="text-xl text-white semibold">
                  {medalsData.cantidad}
                </p>
              </div>
            </div>
            <div
              className={cn(
                'px-2 border-l border-[#3366FF] pl-4 ml-5 space-y-1',
                {
                  'border-gdt-gray-3': pinsData.isPremium,
                }
              )}
            >
              <p className="text-sm text-white font-semibold">Mis Pines</p>
              <div
                className={`flex items-center gap-1 ${
                  !pinsData.isPremium ? 'cursor-pointer' : ''
                }`}
                onClick={() =>
                  !pinsData.isPremium && location.pathname !== ROUTES.myAccount
                    ? navigate(ROUTES.myAccount)
                    : null
                }
              >
                {!pinsData.isPremium && (
                  <p className="text-xl text-white semibold">
                    {pinsData.pinsAvailable}
                  </p>
                )}
                <div>
                  {pinsData.isPremium ? (
                    <PremiumIcon variant="premium" />
                  ) : (
                    <MasPinesIcon variant="quaternary" />
                  )}
                </div>
              </div>
            </div>
            <div
              className={cn('border-l border-[#3366FF] space-y-1 pl-4', {
                'border-gdt-gray-3': pinsData.isPremium,
              })}
              onClick={() => navigate(ROUTES.editAccount.myTeam)}
            >
              <div className="max-w-10 relative cursor-pointer">
                <TshirtTeam
                  shield={dataTshirtShield?.shield}
                  tshirt={dataTshirtShield?.tshirt}
                />
                <span
                  className={cn(
                    'absolute -bottom-[2px] -right-[2px] h-5 w-5 bg-gdt-blue rounded-full flex justify-center items-center border-[1px] border-white',
                    { 'bg-gdt-status-yellow': pinsData.isPremium }
                  )}
                >
                  <PencilIcon className="max-w-[13px] relative top-[-1px] left-[1px]" />
                </span>
              </div>
            </div>
          </div>
        </header>
        <nav className="flex flex-1 flex-col px-8 py-6 gap-y-6 navMobile">
          <ul className="flex flex-1 flex-col gap-y-2" role="list">
            {navigation.map((item) => {
              const goToSection =
                item.href === ROUTES.myTeam5 && !userData?.tiene_equipo_f5
                  ? ROUTES.welcomeF5
                  : item.href;
              return (
                <li key={`menu-item-mobile-${item.name}`}>
                  {!item.children ? (
                    <NavLink
                      className="group flex p-2 text-sm font-medium text-black items-center item--navbar"
                      to={goToSection}
                      onClick={() => handleOpen(false)}
                    >
                      <div className="mr-4 w-[20px]">{item.icon}</div>
                      {item.name}
                      {item.highlight && (
                        <img
                          alt="alert icon"
                          className="ml-2"
                          src={IconAlert}
                        />
                      )}
                    </NavLink>
                  ) : (
                    <Disclosure as="div">
                      {({ open, close }) => (
                        <>
                          <DisclosureButton
                            className={cn(
                              'group flex p-2 text-sm font-medium text-black items-center w-full item--navbar',
                              {
                                active: isActiveLink,
                              }
                            )}
                          >
                            <div className="mr-4 w-[20px]">{item.icon}</div>
                            {item.name}
                            {item.highlight && (
                              <img className="ml-2" src={IconAlert} />
                            )}
                            <FiChevronDown
                              className={cn(
                                'text-black w-4 h-4 transition-all ease-in-out duration-100 ml-2',
                                {
                                  'rotate-180': open,
                                }
                              )}
                            />
                          </DisclosureButton>
                          <DisclosurePanel as="ul" className="mt-1 px-2">
                            {item.children &&
                              item.children.map((subItem) => (
                                <li key={`submenu-item-mobile-${subItem.name}`}>
                                  {!subItem.children ||
                                  (subItem.children && !userData?.showTDAP) ? (
                                    <Link
                                      className="flex items-center py-2 pr-2 pl-4 ml-8 border-l border-gdt-gray-6 text-sm font-medium text-black"
                                      to={subItem.disabled ? '#' : subItem.href}
                                      onClick={() => {
                                        if (subItem.disabled) {
                                          toast.show(
                                            'Muy pronto vas a poder competir en los Desafíos 1 a 1'
                                          );
                                        }
                                        close();
                                        handleOpen(false);
                                      }}
                                    >
                                      <div className="mr-4 w-[20px]">
                                        {subItem.icon}
                                      </div>
                                      {subItem.name}
                                      {subItem.highlight && (
                                        <img
                                          alt="aler icon"
                                          className="ml-2"
                                          src={IconAlert}
                                        />
                                      )}
                                    </Link>
                                  ) : (
                                    userData?.showTDAP && (
                                      <Disclosure as="div">
                                        {({ open, close }) => (
                                          <>
                                            <DisclosureButton className="flex items-center py-2 pr-2 pl-4 ml-8 border-l border-gdt-gray-6 text-sm font-medium text-black">
                                              <div className="mr-4 w-[20px]">
                                                {subItem.icon}
                                              </div>
                                              {subItem.name}
                                              <FiChevronDown
                                                className={cn(
                                                  'text-black w-4 h-4 transition-all ease-in-out duration-100 ml-2',
                                                  {
                                                    'rotate-180': open,
                                                  }
                                                )}
                                              />
                                            </DisclosureButton>
                                            <DisclosurePanel
                                              as="ul"
                                              className="mt-1 mb-2"
                                            >
                                              {subItem.children &&
                                                subItem.children.map(
                                                  (subSubItem, index) => (
                                                    <li
                                                      key={`subSubmenu-item-mobile-${index}${subItem.name}`}
                                                    >
                                                      <NavLink
                                                        className="flex items-center py-2 pr-2 ml-12 text-xs font-medium text-gdt-gray-2 item--navbar"
                                                        to={subSubItem.href}
                                                        onClick={() => {
                                                          close();
                                                          handleOpen(false);
                                                        }}
                                                      >
                                                        {subSubItem.name}
                                                      </NavLink>
                                                    </li>
                                                  )
                                                )}
                                            </DisclosurePanel>
                                          </>
                                        )}
                                      </Disclosure>
                                    )
                                  )}
                                </li>
                              ))}
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  )}
                </li>
              );
            })}
          </ul>
          <div
            aria-hidden="true"
            className="bg-gdt-gray-5 h-[1px] w-full opacity-[0.3]"
          />
          <ul className="flex flex-1 flex-col gap-y-2" role="list">
            {bottomNavigation.map((item) => {
              return (
                <li key={`item-bottom-2-${item.name}`}>
                  <NavLink
                    className={`"group flex p-2 text-sm font-medium text-black items-center " ${
                      !item.onClick ? 'item--navbar' : ''
                    }     `}
                    to={item.href}
                    onClick={() => {
                      item.onClick && item.onClick();
                      handleOpen(false);
                    }}
                  >
                    <div className="mr-4 w-[20px]">{item.icon}</div>
                    {item.name}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </SliderOver>
  );
};

export default Mobile;
